import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, CheckCircle2, Clock3, Loader2, Workflow } from 'lucide-react'
import { Badge } from '@components/ui/Badge'
import { Button } from '@components/ui/Button'
import { useContextStore } from '@/store/context'
import { recipesAPI } from '@/services/api/recipes'
import type { ExecutionRecord } from '@/store/operate'
import type { RecipeExecutionStatus } from '@/services/api/recipes'
import type { MetaRecette } from '@/types/recipe'
import RecipePreviewCanvas from './RecipePreviewCanvas'
import RecipeExecutionUsageSummaryCard from './RecipeExecutionUsageSummaryCard'

interface HistoryDetailPanelProps {
  execution: ExecutionRecord
}

function formatDate(dateString?: string, locale?: string) {
  if (!dateString) return null

  return new Date(dateString).toLocaleString(locale, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatDuration(start?: string, end?: string) {
  if (!start) return null

  const startedAt = new Date(start).getTime()
  const completedAt = end ? new Date(end).getTime() : Date.now()
  const durationMs = completedAt - startedAt

  if (durationMs < 1000) return `${Math.round(durationMs)}ms`
  return `${(durationMs / 1000).toFixed(1)}s`
}

function renderStatusIcon(status: ExecutionRecord['status']) {
  switch (status) {
    case 'completed':
      return <CheckCircle2 className="h-4 w-4 text-green-600" />
    case 'failed':
      return <AlertCircle className="h-4 w-4 text-red-600" />
    case 'running':
      return <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
    case 'pending':
    default:
      return <Clock3 className="h-4 w-4 text-slate-500" />
  }
}

function getExecutionDisplayName(execution: ExecutionRecord) {
  return execution.recipeName || execution.recipeId.replace(/-/g, ' ')
}

export default function HistoryDetailPanel({
  execution,
}: HistoryDetailPanelProps) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const orgId = useContextStore((state) => state.org)
  const deploymentId = useContextStore((state) => state.deploymentId)
  const [recipe, setRecipe] = useState<MetaRecette | null>(null)
  const [detailExecution, setDetailExecution] = useState<RecipeExecutionStatus | null>(null)
  const [loadingRecipe, setLoadingRecipe] = useState(false)
  const [recipeError, setRecipeError] = useState<string | null>(null)

  const renderStatusBadge = (status: ExecutionRecord['status']) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400">{t('historyDetail.completedStatus')}</Badge>
      case 'failed':
        return <Badge className="bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-400">{t('historyDetail.failed')}</Badge>
      case 'running':
        return <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-400">{t('historyDetail.running')}</Badge>
      case 'pending':
      default:
        return <Badge className="bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400">{t('historyDetail.pending')}</Badge>
    }
  }

  useEffect(() => {
    let cancelled = false

    const loadRecipe = async () => {
      if (!orgId || !deploymentId || !execution.recipeId) {
        setRecipe(null)
        setRecipeError(null)
        setLoadingRecipe(false)
        return
      }

      try {
        setLoadingRecipe(true)
        setRecipeError(null)
        const loadedRecipe = await recipesAPI.getRecipe(orgId, deploymentId, execution.recipeId)
        if (!cancelled) {
          setRecipe(loadedRecipe)
        }
      } catch (error) {
        if (!cancelled) {
          setRecipe(null)
          setRecipeError(
            error instanceof Error ? error.message : t('historyDetail.loadingBlueprint'),
          )
        }
      } finally {
        if (!cancelled) {
          setLoadingRecipe(false)
        }
      }
    }

    void loadRecipe()

    return () => {
      cancelled = true
    }
  }, [deploymentId, execution.recipeId, orgId])

  useEffect(() => {
    let cancelled = false

    const loadExecution = async () => {
      if (!orgId || !deploymentId || !execution.executionId) {
        setDetailExecution(null)
        return
      }

      try {
        const loadedExecution = await recipesAPI.getScopedRecipeExecutionStatus(
          orgId,
          deploymentId,
          execution.executionId,
          { include_logs: false, include_trace: false },
        )
        if (!cancelled) {
          setDetailExecution(loadedExecution)
        }
      } catch {
        if (!cancelled) {
          setDetailExecution(null)
        }
      }
    }

    void loadExecution()

    return () => {
      cancelled = true
    }
  }, [deploymentId, execution.executionId, orgId])

  const usageSummary = detailExecution?.usage_summary ?? execution.usageSummary

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="border-b border-border px-4 py-4">
        <div className="flex items-center gap-2">
          {renderStatusIcon(execution.status)}
          <h3 className="text-sm font-semibold">
            {getExecutionDisplayName(execution)}
          </h3>
        </div>
        <div className="mt-2 flex items-center gap-2">
          {renderStatusBadge(execution.status)}
          <code className="rounded bg-muted px-2 py-1 text-[11px]">
            {execution.executionId}
          </code>
        </div>
        {execution.recipeId && (
          <div className="mt-3">
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(`/operate/chat?workflow=${execution.recipeId}&autorunWorkflow=1`)}
            >
              {t('historyDetail.rerun')}
            </Button>
          </div>
        )}
      </div>

      <div className="flex-1 space-y-5 overflow-y-auto px-4 py-4">
        <section className="space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('historyDetail.timeline')}
          </h4>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">{t('historyDetail.created')}</dt>
              <dd>{formatDate(execution.createdAt, i18n.language) || t('historyDetail.notAvailable')}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">{t('historyDetail.started')}</dt>
              <dd>{formatDate(execution.startedAt, i18n.language) || t('historyDetail.notAvailable')}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">{t('historyDetail.completed')}</dt>
              <dd>{formatDate(execution.completedAt, i18n.language) || t('historyDetail.notAvailable')}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">{t('historyDetail.duration')}</dt>
              <dd>{formatDuration(execution.startedAt || execution.createdAt, execution.completedAt) || t('historyDetail.notAvailable')}</dd>
            </div>
          </dl>
        </section>

        <RecipeExecutionUsageSummaryCard summary={usageSummary} />

        {execution.result && (
          <section className="space-y-2">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('chatMessage.result')}
            </h4>
            <pre className="max-h-72 overflow-auto rounded-lg border border-border bg-muted/40 p-3 text-xs">
              {JSON.stringify(execution.result, null, 2)}
            </pre>
          </section>
        )}

        <section className="space-y-2">
          <div className="flex items-center gap-2">
            <Workflow className="h-4 w-4 text-primary" />
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('historyDetail.blueprint')}
            </h4>
          </div>

          {loadingRecipe ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('historyDetail.loadingBlueprint')}
            </div>
          ) : recipe ? (
            <div className="space-y-3">
              <div className="rounded-lg border border-border bg-muted/20 px-3 py-2">
                <div className="text-sm font-medium">{recipe.name}</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {recipe.description || t('historyDetail.noDescription')}
                </div>
              </div>
              <RecipePreviewCanvas recipe={recipe} />
            </div>
          ) : recipeError ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-400">
              {recipeError}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">
              {t('historyDetail.noRecipe')}
            </div>
          )}
        </section>

        {execution.error && (
          <section className="space-y-2">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-red-700 dark:text-red-400">
              {t('historyDetail.error')}
            </h4>
            <pre className="max-h-48 overflow-auto rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800 dark:border-red-800 dark:bg-red-950/50 dark:text-red-400">
              {execution.error}
            </pre>
          </section>
        )}
      </div>
    </div>
  )
}
